import React, { forwardRef, memo } from 'react';
import PropTypes from 'prop-types';
import { orange } from '@mui/material/colors';
import { styled, alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';

const toneToColors = (theme, tone) => {
    if (tone === 'neutral') {
        return {
            main: theme.palette.grey[700],
            bg: alpha(theme.palette.grey[500], 0.16),
            border: theme.palette.grey[400],
        };
    }
    if (tone === 'warning') {
        const main = orange[700];
        return { main, bg: alpha(main, 0.18), border: alpha(main, 0.55) };
    }
    const pal = theme.palette[tone];
    return { main: pal.main, bg: alpha(pal.main, 0.12), border: alpha(pal.main, 0.5) };
};

const commonStyles = (theme, $tone, $variant, $size, interactive) => {
    const c = toneToColors(theme, $tone);
    const paddY = $size === 'small' ? 2 : 3;
    const paddX = $size === 'small' ? 6 : 8;
    const radius = 16;

    return {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 2,
        borderRadius: radius,
        padding: `${paddY}px ${paddX}px`,
        fontWeight: 600,
        lineHeight: 1,
        whiteSpace: 'nowrap',
        border: '1px solid transparent',
        ...($variant === 'filled'
            ? {
                  backgroundColor: c.bg,
                  color: $tone === 'neutral' ? theme.palette.text.primary : c.main,
              }
            : {
                  backgroundColor: theme.palette.common.white,
                  color: c.main,
                  borderColor: c.border,
              }),
        '& .ind-icon': {
            display: 'inline-flex',
            marginTop: -1,
            lineHeight: 0,
            '& > svg': {
                fontSize: $size === 'small' ? 12 : 16,
            },
        },
        '& .ind-count': {
            minWidth: 14,
            height: 14,
            borderRadius: 6,
            // padding: '0 4px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 9,
            fontWeight: 700,
            backgroundColor: $variant === 'filled' ? alpha(c.main, 0.15) : alpha(c.main, 0.1),
            color: c.main,
        },
        ...(interactive && {
            cursor: 'pointer',
            transition: 'background-color 120ms ease, box-shadow 120ms ease, transform 60ms ease',
            '&:hover': {
                backgroundColor: $variant === 'filled' ? alpha(c.main, 0.18) : alpha(c.main, 0.06),
            },
            '&:active': { transform: 'translateY(0.5px)' },
            '&:focus-visible': { outline: 'none', boxShadow: `0 0 0 2px ${alpha(c.main, 0.4)}` },
            '&:disabled': { cursor: 'not-allowed', opacity: 0.55, boxShadow: 'none' },
        }),
    };
};

const SpanRoot = styled('span', { shouldForwardProp: p => !p.startsWith('$') })(
    ({ theme, $tone, $variant, $size }) => commonStyles(theme, $tone, $variant, $size, false)
);

const ButtonRoot = styled('button', { shouldForwardProp: p => !p.startsWith('$') })(
    ({ theme, $tone, $variant, $size }) => ({
        appearance: 'none',
        border: 0,
        background: 'none',
        padding: 0,
        margin: 0,
        font: 'inherit',
        textAlign: 'inherit',
        ...commonStyles(theme, $tone, $variant, $size, true),
    })
);

const IndicatorInner = forwardRef(function IndicatorInner(
    {
        label,
        tone,
        variant = 'filled',
        size = 'small',
        count,
        IconEl,
        iconPosition = 'start',
        className,
        style,
        clickable = false,
        onClick,
        disabled = false,
        ariaLabel,
    },
    ref
) {
    const Pill = clickable ? ButtonRoot : SpanRoot;
    const showCount = typeof count === 'number';
    const showIcon = Boolean(IconEl);
    const iconFirst = iconPosition !== 'end';
    const a11yLabel = ariaLabel || label;

    return (
        <Pill
            ref={ref}
            type={clickable ? 'button' : undefined}
            $tone={tone}
            $variant={variant}
            $size={size}
            className={className}
            style={style}
            onClick={clickable && typeof onClick === 'function' ? onClick : undefined}
            disabled={clickable ? disabled : undefined}
            aria-label={clickable ? a11yLabel : undefined}
            data-indicator
        >
            {showCount && <span className="ind-count">{count}</span>}
            {iconFirst && showIcon && <span className="ind-icon">{IconEl}</span>}
            <Typography component="span" variant={size === 'small' ? 'body2' : 'body1'}>
                {label}
            </Typography>
            {!iconFirst && showIcon && <span className="ind-icon">{IconEl}</span>}
        </Pill>
    );
});

export const Indicator = memo(function Indicator({ tooltip, ...props }) {
    if (!tooltip) return <IndicatorInner {...props} />;
    return (
        <Tooltip title={tooltip} arrow>
            <span>
                {/* span wrapper ensures Tooltip works with disabled buttons */}
                <IndicatorInner {...props} />
            </span>
        </Tooltip>
    );
});

Indicator.propTypes = {
    label: PropTypes.string.isRequired,
    tone: PropTypes.oneOf(['success', 'info', 'warning', 'error', 'neutral']).isRequired,
    variant: PropTypes.oneOf(['filled', 'outlined']),
    size: PropTypes.oneOf(['small', 'medium']),
    count: PropTypes.number,
    IconEl: PropTypes.node,
    iconPosition: PropTypes.oneOf(['start', 'end']),
    className: PropTypes.string,
    style: PropTypes.object,
    clickable: PropTypes.bool,
    onClick: PropTypes.func,
    disabled: PropTypes.bool,
    ariaLabel: PropTypes.string,
    tooltip: PropTypes.node,
};

export default Indicator;
